import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { UniversalViewer } from '@xtnd/web/shared/components/UniversalViewer';
import { documentApi } from '../api';
import type { DocumentType } from '../types';
import { Icon } from '@repo/ui';

/**
 * DocumentViewer Page
 *
 * Displays documents (PDFs, receipts, etc.) using the UniversalViewer component
 * Fetches signed URLs from the backend API
 *
 * URL format: /juniper/document-viewer/:juniperId?type=invoice&filename=...
 */
export function DocumentViewerPage() {
  const { juniperId } = useParams<{ juniperId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const documentType = searchParams.get('type') as DocumentType | null;
  const filename = searchParams.get('filename') || undefined;

  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mimeType, setMimeType] = useState<string | undefined>();

  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!juniperId || !documentType) {
        setError('Missing required parameters: juniperId and type');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await documentApi.getSignedUrl(
          juniperId,
          documentType,
          filename
        );

        setSignedUrl(response.signedUrl);

        // Determine MIME type
        const mime = filename
          ? documentApi.getMimeType(filename)
          : documentApi.getMimeType(response.filename);
        setMimeType(mime);

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load document';
        console.error('Error fetching signed URL:', err);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [juniperId, documentType, filename]);

  const handleClose = () => {
    // Navigate back to transactions page
    navigate('/juniper/transactions');
  };

  // Loading state
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-2 border-xtnd-blue mb-4"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300">Loading document...</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {documentType && `Type: ${documentType}`}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !signedUrl) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-900 p-6">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8 text-center">
          <Icon name="HiExclamationCircle" className="h-20 w-20 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3">
            Failed to Load Document
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {error || 'Document not found or unavailable'}
          </p>
          <div className="space-x-3">
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-2 text-sm font-medium text-white bg-xtnd-blue hover:bg-xtnd-blue-600 rounded-md transition-colors"
            >
              Try Again
            </button>
            <button
              onClick={handleClose}
              className="px-6 py-2 text-sm font-medium text-xtnd-dark hover:text-xtnd-dark-600 border border-xtnd-dark hover:border-xtnd-dark-600 rounded-md transition-colors"
            >
              Back to Transactions
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Document viewer
  return (
    <div className="h-screen bg-gray-100 dark:bg-gray-900 p-4">
      <UniversalViewer
        signedUrl={signedUrl}
        mimeType={mimeType}
        fileName={filename || `${documentType}.pdf`}
        className="h-full"
        onClose={handleClose}
      />
    </div>
  );
}

